import apiClient from '../config/axiosConfig';
import { API_BASE_URL } from '../utils/apiUtils';

const validateEmail = (email) => {
    if (!email || typeof email !== 'string' || email.trim() === '') {
        throw new Error('Email is required and must be a valid string');
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
        throw new Error('Email must be in a valid format');
    }

    return email.trim().toLowerCase();
};

const validateName = (name) => {
    if (!name || typeof name !== 'string' || name.trim() === '') {
        throw new Error('Name is required and must be a valid string');
    }
    return name.trim();
};

const validateToken = (token) => {
    if (!token || typeof token !== 'string' || token.trim() === '') {
        throw new Error('Authentication token is required');
    }
    return token.trim();
};

const validateFeatureRequest = (featureRequest) => {
    if (!featureRequest || typeof featureRequest !== 'string' || featureRequest.trim() === '') {
        throw new Error('Feature request is required and must be a valid string');
    }
    return featureRequest.trim();
};

const validateSupportFormData = (formData) => {
    if (!formData) {
        throw new Error('Support form data is required');
    }

    if (formData instanceof FormData) {
        const subject = formData.get('subject');
        const message = formData.get('message');

        if (!subject || typeof subject !== 'string' || subject.trim() === '') {
            throw new Error('Subject is required');
        }
        if (!message || typeof message !== 'string' || message.trim() === '') {
            throw new Error('Message is required');
        }

        return formData;
    } else {
        if (!formData.subject || typeof formData.subject !== 'string' || formData.subject.trim() === '') {
            throw new Error('Subject is required');
        }
        if (!formData.message || typeof formData.message !== 'string' || formData.message.trim() === '') {
            throw new Error('Message is required');
        }

        const newFormData = new FormData();
        newFormData.append('subject', formData.subject.trim());
        newFormData.append('message', formData.message.trim());

        if (formData.file) {
            newFormData.append('file', formData.file);
        }

        return newFormData;
    }
};

const validateLeadData = (leadData) => {
    if (!leadData || typeof leadData !== 'object') {
        throw new Error('Lead data must be a valid object');
    }

    if (!leadData.email || typeof leadData.email !== 'string' || leadData.email.trim() === '') {
        throw new Error('Lead email is required');
    }

    return {
        ...leadData,
        email: validateEmail(leadData.email),
        name: leadData.name ? validateName(leadData.name) : undefined,
        phone: leadData.phone ? validateStringValue(leadData.phone, 'Phone') : undefined,
        company: leadData.company ? validateStringValue(leadData.company, 'Company') : undefined
    };
};

const validateCardData = (cardData) => {
    if (!cardData || typeof cardData !== 'object') {
        throw new Error('Card data must be a valid object');
    }

    return {
        ...cardData,
        name: cardData.name ? validateStringValue(cardData.name, 'Card name') : undefined,
        email: cardData.email ? validateEmail(cardData.email) : undefined,
        phone: cardData.phone ? validateStringValue(cardData.phone, 'Card phone') : undefined
    };
};

const validateEmailData = (emailData) => {
    if (!emailData || typeof emailData !== 'object') {
        throw new Error('Email data must be a valid object');
    }

    if (!emailData.to || typeof emailData.to !== 'string' || emailData.to.trim() === '') {
        throw new Error('Recipient email is required');
    }

    if (!emailData.subject || typeof emailData.subject !== 'string' || emailData.subject.trim() === '') {
        throw new Error('Email subject is required');
    }

    return {
        ...emailData,
        to: validateEmail(emailData.to),
        subject: emailData.subject.trim(),
        text: emailData.text ? validateStringValue(emailData.text, 'Email text') : undefined,
        html: emailData.html ? validateStringValue(emailData.html, 'Email HTML') : undefined,
        scheduledDate: emailData.scheduledDate ? validateDate(emailData.scheduledDate) : undefined,
        templateId: emailData.templateId ? validateStringValue(emailData.templateId, 'Template ID') : undefined
    };
};

const validateStringValue = (value, fieldName) => {
    if (value !== null && value !== undefined && typeof value !== 'string') {
        throw new Error(`${fieldName} must be a string`);
    }
    return value?.trim();
};

const validateDate = (date) => {
    if (!date) return undefined;

    if (typeof date === 'string') {
        const parsedDate = new Date(date);
        if (isNaN(parsedDate.getTime())) {
            throw new Error('Date must be in a valid format');
        }
        return date;
    }

    if (date instanceof Date) {
        if (isNaN(date.getTime())) {
            throw new Error('Date must be valid');
        }
        return date.toISOString();
    }

    throw new Error('Date must be a valid date string or Date object');
};

const handleEmailAuthError = (error, operation = 'API operation') => {
    if (error.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
        throw new Error('Your session has expired. Please log in again');
    }

    if (error.response?.status === 403) {
        throw new Error('You do not have permission to send emails');
    }

    if (error.response?.status === 404) {
        throw new Error('Email service not found or no longer available');
    }

    if (error.response?.status === 409) {
        throw new Error('Email already sent or conflicts with existing data');
    }

    if (error.response?.status === 422) {
        const validationErrors = error.response?.data?.errors || [];
        const errorMessage = validationErrors.length > 0
            ? `Validation errors: ${validationErrors.join(', ')}`
            : 'Invalid email data provided';
        throw new Error(errorMessage);
    }

    if (error.response?.status === 429) {
        throw new Error('Too many email requests. Please try again later.');
    }

    if (error.response?.status >= 500) {
        throw new Error('Email service error. Please try again later.');
    }

    if (error.code === 'ERR_NETWORK') {
        throw new Error('Network error. Please check your connection and try again.');
    }

    if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout. Please try again.');
    }

    const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
    throw new Error(errorMessage);
};

export const sendFeatureRequest = async (featureRequest, token) => {
    try {
        const validFeatureRequest = validateFeatureRequest(featureRequest);
        const validToken = validateToken(token);

        const response = await apiClient.post(
            `${API_BASE_URL}/api/email/feature-request`,
            { featureRequest: validFeatureRequest },
            {
                headers: {
                    Authorization: `Bearer ${validToken}`
                }
            }
        );

        if (response.status === 200 || response.status === 201) {
            return {
                success: true,
                message: response.data.message
            };
        }
        throw new Error('Failed to send feature request');
    } catch (error) {
        handleEmailAuthError(error, 'Sending feature request');
    }
};

export const sendWelcomeEmail = async (userEmail, userName) => {
    try {
        const validUserEmail = validateEmail(userEmail);
        const validUserName = validateName(userName);

        const response = await apiClient.post(
            `${API_BASE_URL}/api/email/welcome`,
            { userEmail: validUserEmail, userName: validUserName }
        );

        if (response.status === 200 || response.status === 201) {
            return {
                success: true,
                message: response.data.message
            };
        }
        throw new Error('Failed to send welcome email');
    } catch (error) {
        handleEmailAuthError(error, 'Sending welcome email');
    }
};

export const sendSupportEmail = async (formData, token) => {
    try {
        const validFormData = validateSupportFormData(formData);
        const validToken = validateToken(token);

        const response = await apiClient.post(
            `${API_BASE_URL}/api/email/support`,
            validFormData,
            {
                headers: {
                    Authorization: `Bearer ${validToken}`,
                    'Content-Type': 'multipart/form-data'
                }
            }
        );

        if (response.status === 200 || response.status === 201) {
            return {
                success: true,
                message: response.data.message
            };
        }
        throw new Error('Failed to send support email');
    } catch (error) {
        handleEmailAuthError(error, 'Sending support email');
    }
};

export const sendFollowUpEmail = async (leadData, cardData, token) => {
    try {
        const validLeadData = validateLeadData(leadData);
        const validCardData = validateCardData(cardData);
        const validToken = validateToken(token);

        const response = await apiClient.post(
            `${API_BASE_URL}/api/email/follow-up-email`,
            { leadData: validLeadData, cardData: validCardData },
            {
                headers: {
                    Authorization: `Bearer ${validToken}`
                }
            }
        );

        if (response.status === 200 || response.status === 201) {
            return {
                success: true,
                message: response.data.message
            };
        }
        throw new Error('Failed to send follow-up email');
    } catch (error) {
        handleEmailAuthError(error, 'Sending follow-up email');
    }
};

export const sendTemplateEmail = async (emailData, token) => {
    try {
        const validEmailData = validateEmailData(emailData);
        const validToken = validateToken(token);

        const response = await apiClient.post(
            `${API_BASE_URL}/api/email/send-template`,
            validEmailData,
            {
                headers: {
                    Authorization: `Bearer ${validToken}`
                }
            }
        );

        if (response.status === 200 || response.status === 201) {
            return {
                success: true,
                message: response.data.message
            };
        }
        throw new Error('Failed to send template email');
    } catch (error) {
        handleEmailAuthError(error, 'Sending template email');
    }
};

export const sendLeadEmail = async (emailData, token) => {
    try {
        const validEmailData = validateEmailData(emailData);
        const validToken = validateToken(token);

        const response = await apiClient.post(
            `${API_BASE_URL}/api/email/send-lead-email`,
            {
                to: validEmailData.to,
                subject: validEmailData.subject,
                text: validEmailData.text,
                scheduledDate: validEmailData.scheduledDate,
                templateId: validEmailData.templateId || 'd-244a3670aac94d86b4c62b8c2a4316a1'
            },
            {
                headers: {
                    Authorization: `Bearer ${validToken}`
                }
            }
        );

        if (response.status === 200 || response.status === 201) {
            return {
                success: true,
                message: response.data.message
            };
        }
        throw new Error('Failed to send lead email');
    } catch (error) {
        handleEmailAuthError(error, 'Sending lead email');
    }
};